import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdEvent,
  MdLocationOn,
  MdDateRange,
  MdSave,
  MdCancel,
  MdPhoto,
  MdCalendarMonth,
  MdInfo,
  MdGridView,
  MdViewList,
  MdClose,
  MdNavigateBefore,
  MdNavigateNext,
  MdFirstPage,
  MdLastPage,
  MdSearch,
  MdFilterList,
  MdClear,
  MdVisibility,
} from "react-icons/md";
import EventService from "service/event.service";
import Modal from "components/modal";
import { usePhotoUrl } from "hooks/usePhotoUrl";
import EventDetails from "./components/EventDetails";

// Fonctions utilitaires
const statusDate = (dateDebut, dateFin) => {
  const now = new Date();
  const startDate = new Date(dateDebut);
  const endDate = new Date(dateFin);
  if (startDate > now) {
    return "À venir";
  } else if (endDate < now) {
    return "Terminé";
  } else {
    return "En cours";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "À venir":
      return "bg-blue-500";
    case "En cours":
      return "bg-green-500";
    case "Terminé":
      return "bg-gray-500";
    default:
      return "bg-[#d4af37]/80";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("fr-FR", options);
};

// Composant principal Events
const Events = () => {
  // États
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dateDebut: "",
    dateFin: "",
    location: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  // État pour la visualisation détaillée
  const [detailView, setDetailView] = useState(false);
  const [eventToView, setEventToView] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 3;

  // Recherche et filtrage
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // 'all', 'upcoming', 'ongoing', 'past'
  const [showFilters, setShowFilters] = useState(false);

  // Utilisation du hook personnalisé pour gérer les URLs des photos
  const { getPhotoUrl } = usePhotoUrl();

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Filtrage des événements lorsque les critères changent
  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, filterStatus]);

  // Effet pour mettre à jour le nombre total de pages lorsque les événements filtrés changent
  useEffect(() => {
    setTotalPages(Math.ceil(filteredEvents.length / itemsPerPage));
    // S'assurer que la page actuelle n'est pas supérieure au total des pages
    if (
      currentPage > Math.ceil(filteredEvents.length / itemsPerPage) &&
      filteredEvents.length > 0
    ) {
      setCurrentPage(1); // Retour à la première page lors d'un changement de filtre
    }
  }, [filteredEvents, currentPage]);

  // Événements de la page courante (mémorisés)
  const currentEvents = useMemo(() => {
    return filteredEvents.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredEvents, currentPage, itemsPerPage]);

  // Gestionnaires d'événements
  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const data = await EventService.getAllEvents();
      setEvents(data);
      setFilteredEvents(data); // Initialiser filteredEvents avec tous les événements
    } catch (error) {
      console.error("Erreur lors de la récupération des événements:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de récupérer les événements. Veuillez réessayer plus tard.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterEvents = () => {
    let results = [...events];

    // Filtrage par statut
    if (filterStatus !== "all") {
      const now = new Date();

      results = results.filter((event) => {
        const startDate = new Date(event.dateDebut);
        const endDate = new Date(event.dateFin);

        if (filterStatus === "upcoming") {
          return startDate > now;
        } else if (filterStatus === "ongoing") {
          return startDate <= now && endDate >= now;
        } else if (filterStatus === "past") {
          return endDate < now;
        }
        return true;
      });
    }

    // Filtrage par terme de recherche
    if (searchTerm.trim() !== "") {
      const searchTermLower = searchTerm.toLowerCase().trim();
      results = results.filter(
        (event) =>
          event.title.toLowerCase().includes(searchTermLower) ||
          (event.description &&
            event.description.toLowerCase().includes(searchTermLower)) ||
          event.location.toLowerCase().includes(searchTermLower)
      );
    }

    setFilteredEvents(results);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        photo: file,
      });

      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      dateDebut: "",
      dateFin: "",
      location: "",
      photo: null,
    });
    setPhotoPreview(null);
    setSelectedEvent(null);
  };

  const openForm = (event = null) => {
    if (event) {
      // Format dates for form inputs (YYYY-MM-DD)
      const startDate = new Date(event.dateDebut).toISOString().split("T")[0];
      const endDate = new Date(event.dateFin).toISOString().split("T")[0];

      setFormData({
        title: event.title,
        description: event.description,
        dateDebut: startDate,
        dateFin: endDate,
        location: event.location,
        photo: null, // We can't set the file input value for security reasons
      });
      setSelectedEvent(event);
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const viewEventDetails = (event) => {
    setEventToView(event);
    setDetailView(true);
  };

  const closeEventDetails = () => {
    setEventToView(null);
    setDetailView(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Validate form data
    if (
      !formData.title ||
      !formData.dateDebut ||
      !formData.dateFin ||
      !formData.location
    ) {
      Swal.fire({
        icon: "warning",
        title: "Champs requis",
        text: "Veuillez remplir tous les champs obligatoires.",
        confirmButtonColor: "#2ca01c",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      if (selectedEvent) {
        // Update existing event
        await EventService.updateEvent(selectedEvent.id, formData);
        setIsSubmitting(false);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "L'événement a été mis à jour avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      } else {
        // Create new event
        await EventService.createEvent(formData);
        setIsSubmitting(false);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "L'événement a été créé avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      }

      // Refresh the events list
      fetchEvents();
      closeForm();
    } catch (error) {
      setIsSubmitting(false);
      console.error("Erreur lors de l'enregistrement de l'événement:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Une erreur est survenue lors de l'enregistrement de l'événement.",
        confirmButtonColor: "#2ca01c",
      });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2ca01c",
      cancelButtonColor: "#e21b1b",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await EventService.deleteEvent(id);
          setEvents(events.filter((event) => event.id !== id));
          setFilteredEvents(filteredEvents.filter((event) => event.id !== id));
          Swal.fire({
            title: "Supprimé!",
            text: "L'événement a été supprimé avec succès.",
            icon: "success",
            confirmButtonColor: "#2ca01c",
          });
        } catch (error) {
          console.error("Erreur lors de la suppression de l'événement:", error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Une erreur est survenue lors de la suppression de l'événement.",
            confirmButtonColor: "#2ca01c",
          });
        }
      }
    });
  };

  const handlePhotoUploadClick = () => {
    document.getElementById("photo").click();
  };

  const handleKeyDown = (e) => {
    // Activer le téléchargement de photo lorsque l'utilisateur appuie sur Entrée ou Espace
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      document.getElementById("photo").click();
    }
  };

  // Fonction pour changer le mode d'affichage
  const toggleViewMode = () => {
    setViewMode(viewMode === "grid" ? "list" : "grid");
  };

  // Fonctions de pagination
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(currentPage - 1);
  const goToNextPage = () => setCurrentPage(currentPage + 1);
  const goToLastPage = () => setCurrentPage(totalPages);

  // Composants de rendu

  // Rendu d'un événement en mode liste
  const renderListItem = (event) => {
    const status = statusDate(event.dateDebut, event.dateFin);
    const statusColorClass = getStatusColor(status);

    return (
      <div
        key={event.id}
        className="mb-4 overflow-hidden rounded-lg bg-white  transition-all duration-300 hover:shadow-lg"
      >
        <div className="flex flex-col md:flex-row">
          {event.photo ? (
            <div className="h-48 w-full md:h-auto md:w-64">
              <img
                src={getPhotoUrl(event.photo)}
                alt={event.title}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10 md:h-auto md:w-64">
              <MdEvent className="text-6xl text-[#2ca01c]/40" />
            </div>
          )}

          <div className="flex flex-1 flex-col p-5">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#333333]">
                {event.title}
              </h2>
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
            </div>

            <p className="mb-4 line-clamp-2 text-gray-600">
              {event.description || "Aucune description fournie"}
            </p>

            <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg bg-[#f9f9f9] p-3 sm:grid-cols-2">
              <p className="flex items-center text-gray-700">
                <MdDateRange className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Période:</span>
                <span className="ml-2 text-sm">
                  {formatDate(event.dateDebut)} - {formatDate(event.dateFin)}
                </span>
              </p>
              <p className="flex items-center text-gray-700">
                <MdLocationOn className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Lieu:</span>
                <span className="ml-2 text-sm">{event.location}</span>
              </p>
            </div>

            <div className="mt-auto flex space-x-3">
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
                onClick={() => viewEventDetails(event)}
              >
                <MdVisibility className="mr-1" /> Détails
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
                onClick={() => openForm(event)}
              >
                <MdEdit className="mr-1" /> Modifier
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
                onClick={() => handleDelete(event.id)}
              >
                <MdDelete className="mr-1" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Rendu d'un événement en mode grille
  const renderGridItem = (event) => {
    const status = statusDate(event.dateDebut, event.dateFin);
    const statusColorClass = getStatusColor(status);

    return (
      <div
        key={event.id}
        className="overflow-hidden rounded-lg bg-white  transition-all duration-300 hover:shadow-xl"
      >
        {event.photo ? (
          <div className="relative h-48 overflow-hidden">
            <img
              src={getPhotoUrl(event.photo)}
              alt={event.title}
              className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            />
            <div className="from-black/60 to-transparent absolute bottom-0 left-0 w-full bg-gradient-to-t p-3">
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
            </div>
          </div>
        ) : (
          <div className="relative flex h-48 items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10">
            <MdEvent className="text-6xl text-[#2ca01c]/40" />
            <div className="from-black/60 to-transparent absolute bottom-0 left-0 w-full bg-gradient-to-t p-3">
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
            </div>
          </div>
        )}
        <div className="p-5">
          <h2 className="mb-3 border-b border-gray-100 pb-2 text-xl font-semibold text-[#333333]">
            {event.title}
          </h2>
          <p className="mb-5 line-clamp-3 text-gray-600">
            {event.description || "Aucune description fournie"}
          </p>
          <div className="mb-6 space-y-2 rounded-lg bg-[#f9f9f9] p-3">
            <p className="flex items-center text-gray-700">
              <MdDateRange className="mr-2 text-[#7028a9]" />
              <span className="font-medium">Période:</span>
              <span className="ml-2">
                {formatDate(event.dateDebut)} - {formatDate(event.dateFin)}
              </span>
            </p>
            <p className="flex items-center text-gray-700">
              <MdLocationOn className="mr-2 text-[#7028a9]" />
              <span className="font-medium">Lieu:</span>
              <span className="ml-2">{event.location}</span>
            </p>
          </div>
          <div className="flex flex-col space-y-2">
            <button
              className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
              onClick={() => viewEventDetails(event)}
            >
              <MdVisibility className="mr-1" /> Voir les détails
            </button>
            <div className="flex space-x-3">
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
                onClick={() => openForm(event)}
              >
                <MdEdit className="mr-1" /> Modifier
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
                onClick={() => handleDelete(event.id)}
              >
                <MdDelete className="mr-1" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Rendu de la pagination
  const renderPagination = () => {
    return (
      <div className="mt-8 flex items-center justify-center">
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={goToFirstPage}
            disabled={currentPage === 1}
            className={`${
              currentPage === 1
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center rounded-l-md border border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Première page"
          >
            <MdFirstPage />
          </button>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className={`${
              currentPage === 1
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Page précédente"
          >
            <MdNavigateBefore />
          </button>

          <div className="flex items-center border-b border-r border-t border-gray-300 bg-white px-4 py-2 text-sm font-medium">
            <span className="mx-2 font-medium text-gray-700">
              Page {currentPage} sur {totalPages}
            </span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`${
              currentPage === totalPages || totalPages === 0
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Page suivante"
          >
            <MdNavigateNext />
          </button>
          <button
            onClick={goToLastPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`${
              currentPage === totalPages || totalPages === 0
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center rounded-r-md border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Dernière page"
          >
            <MdLastPage />
          </button>
        </div>
      </div>
    );
  };

  // Rendu des filtres et recherche
  const renderSearchFilters = () => {
    return (
      <div className="mb-6 rounded-lg bg-white p-4 ">
        <div className="mb-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          {/* Champ de recherche */}
          <div className="relative w-full max-w-md">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 transform text-lg text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un événement..."
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                aria-label="Effacer la recherche"
              >
                <MdClear />
              </button>
            )}
          </div>

          {/* Bouton filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center rounded-md border ${
              showFilters
                ? "border-[#2ca01c] bg-[#2ca01c]/10 text-[#2ca01c]"
                : "border-gray-300 bg-white text-gray-700"
            } px-4 py-2 transition-colors`}
          >
            <MdFilterList className="mr-2" />
            Filtres{" "}
            {filterStatus !== "all" && (
              <span className="ml-1 rounded-full bg-[#2ca01c] px-2 text-white">
                1
              </span>
            )}
          </button>

          {/* Bouton réinitialiser */}
          {(searchTerm || filterStatus !== "all") && (
            <button
              onClick={resetFilters}
              className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              <MdClear className="mr-2" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Options de filtrage par statut */}
        {showFilters && (
          <div className="animate-fadeIn mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 font-medium text-gray-700">
              Filtrer par statut:
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus("all")}
                className={`rounded-md px-3 py-1 text-sm ${
                  filterStatus === "all"
                    ? "bg-[#2ca01c] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setFilterStatus("upcoming")}
                className={`rounded-md px-3 py-1 text-sm ${
                  filterStatus === "upcoming"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                À venir
              </button>
              <button
                onClick={() => setFilterStatus("ongoing")}
                className={`rounded-md px-3 py-1 text-sm ${
                  filterStatus === "ongoing"
                    ? "bg-green-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                En cours
              </button>
              <button
                onClick={() => setFilterStatus("past")}
                className={`rounded-md px-3 py-1 text-sm ${
                  filterStatus === "past"
                    ? "bg-gray-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Terminé
              </button>
            </div>
          </div>
        )}

        {/* Résumé des résultats */}
        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-sm text-gray-500">
          <p>
            {filteredEvents.length} événement
            {filteredEvents.length !== 1 ? "s" : ""} trouvé
            {filteredEvents.length !== 1 ? "s" : ""}
            {searchTerm || filterStatus !== "all"
              ? " avec les filtres actuels"
              : ""}
          </p>

          {filteredEvents.length > 0 && (
            <div className="flex items-center">
              <p className="text-xs text-gray-500">
                Affichage{" "}
                {Math.min(
                  (currentPage - 1) * itemsPerPage + 1,
                  filteredEvents.length
                )}{" "}
                - {Math.min(currentPage * itemsPerPage, filteredEvents.length)}{" "}
                sur {filteredEvents.length}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Contenu du formulaire pour la modal
  const formContent = (
    <>
      {/* En-tête du formulaire */}
      <div className="mb-5 border-b border-gray-200 pb-5">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#333333]">
            {selectedEvent ? "Modifier l'événement" : "Ajouter un événement"}
          </h2>
          <button
            onClick={closeForm}
            className="rounded-full bg-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-300"
            aria-label="Fermer"
          >
            <MdClose size={20} />
          </button>
        </div>
      </div>

      {/* Corps du formulaire */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="title"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Titre <span className="text-[#e21b1b]">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            placeholder="Titre de l'événement"
            required
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            placeholder="Description détaillée de l'événement"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label
              htmlFor="dateDebut"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Date de début <span className="text-[#e21b1b]">*</span>
            </label>
            <input
              type="date"
              id="dateDebut"
              name="dateDebut"
              value={formData.dateDebut}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              required
            />
          </div>

          <div>
            <label
              htmlFor="dateFin"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Date de fin <span className="text-[#e21b1b]">*</span>
            </label>
            <input
              type="date"
              id="dateFin"
              name="dateFin"
              value={formData.dateFin}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="location"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Lieu <span className="text-[#e21b1b]">*</span>
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            placeholder="Lieu de l'événement"
            required
          />
        </div>

        <div>
          <label
            htmlFor="photo"
            className="mb-1 flex items-center text-sm font-semibold text-gray-700"
          >
            <MdPhoto className="mr-1 text-[#7028a9]" /> Photo
          </label>
          <div className="flex items-center">
            {/* Bouton d'upload amélioré pour l'accessibilité */}
            <button
              type="button"
              onClick={handlePhotoUploadClick}
              onKeyDown={handleKeyDown}
              tabIndex="0"
              aria-label="Choisir une photo"
              role="button"
              className="flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2ca01c]"
            >
              <MdPhoto className="mb-2 text-3xl text-gray-400" />
              <span className="text-sm text-gray-500">
                Cliquez ou glissez une image
              </span>
            </button>
            <input
              type="file"
              id="photo"
              name="photo"
              onChange={handlePhotoChange}
              accept="image/*"
              className="hidden"
              aria-hidden="true"
            />
          </div>
          {photoPreview && (
            <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Aperçu de l'image sélectionnée"
                  className="h-48 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPhotoPreview(null);
                    setFormData({
                      ...formData,
                      photo: null,
                    });
                    document.getElementById("photo").value = "";
                  }}
                  className="absolute right-2 top-2 rounded-full bg-white/80 p-1 text-gray-700 hover:bg-white"
                  aria-label="Supprimer l'image"
                >
                  <MdCancel />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Boutons du formulaire */}
        <div className="mt-8 flex justify-end space-x-3 border-t pt-5">
          <button
            type="button"
            onClick={closeForm}
            className="flex items-center rounded-md border border-gray-300 bg-white px-5 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-100"
          >
            <MdCancel className="mr-2" /> Annuler
          </button>

          <button
            type="submit"
            className="flex items-center justify-center rounded-md bg-gradient-to-r from-[#2ca01c] to-[#238615] px-5 py-2 text-white  transition-all duration-200 hover:from-[#238615] hover:to-[#1c6e11] hover:shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="mr-2 h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
                Traitement...
              </>
            ) : (
              <>
                <MdSave className="mr-2" />
                {selectedEvent ? "Mettre à jour" : "Enregistrer"}
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );

  // Rendu principal
  return (
    <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {detailView && eventToView ? (
        <div className="rounded-lg bg-white p-6 ">
          <EventDetails
            event={eventToView}
            onClose={closeEventDetails}
            getPhotoUrl={getPhotoUrl}
          />
        </div>
      ) : (
        <>
          {/* Events header with view mode toggle */}
          <div className="flex flex-col items-start justify-between rounded-lg bg-white p-6  sm:flex-row sm:items-center">
            <h1 className="mb-4 flex items-center text-3xl font-bold text-[#333333] sm:mb-0">
              <div className="mr-4 rounded-full bg-[#2ca01c] p-2 text-white">
                <MdCalendarMonth className="text-3xl" />
              </div>
              Événements
              <span className="ml-2 text-sm font-normal text-gray-500">
                Centre Hospitalier Universitaire Tambohobe
              </span>
            </h1>
            <div className="flex w-full flex-col items-start space-y-3 sm:w-auto sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
              {/* Toggle view mode button */}
              <button
                className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 shadow-sm transition-colors hover:bg-gray-50"
                onClick={toggleViewMode}
                title={
                  viewMode === "grid"
                    ? "Passer à la vue liste"
                    : "Passer à la vue grille"
                }
              >
                {viewMode === "grid" ? (
                  <>
                    <MdViewList className="mr-2 text-[#7028a9]" /> Vue liste
                  </>
                ) : (
                  <>
                    <MdGridView className="mr-2 text-[#7028a9]" /> Vue grille
                  </>
                )}
              </button>

              {/* Add event button */}
              <button
                className="group flex items-center rounded-full bg-[#2ca01c] px-6 py-3 text-white  transition-all duration-300 hover:bg-[#238615] hover:shadow-lg"
                onClick={() => openForm()}
              >
                <MdAdd className="mr-2 rounded-full bg-white text-xl text-[#2ca01c] group-hover:bg-[#f0f0f0]" />
                Ajouter un événement
              </button>
            </div>
          </div>

          {/* Recherche et filtres */}
          {renderSearchFilters()}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-b-transparent h-16 w-16 animate-spin rounded-full border-4 border-[#2ca01c]"></div>
            </div>
          ) : (
            <>
              {filteredEvents.length === 0 ? (
                <div className="col-span-full rounded-lg bg-white p-8 text-center ">
                  <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-green-50">
                    <MdInfo className="text-5xl text-[#2ca01c]" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    {events.length === 0
                      ? "Aucun événement disponible"
                      : "Aucun événement ne correspond à vos critères de recherche"}
                  </h3>
                  <p className="text-gray-600">
                    {events.length === 0
                      ? 'Ajoutez votre premier événement en cliquant sur le bouton "Ajouter un événement"'
                      : "Veuillez essayer avec différents termes de recherche ou filtres"}
                  </p>
                  {events.length > 0 &&
                    (searchTerm || filterStatus !== "all") && (
                      <button
                        onClick={resetFilters}
                        className="mt-4 inline-flex items-center rounded-md bg-[#2ca01c] px-4 py-2 text-white"
                      >
                        <MdClear className="mr-2" /> Réinitialiser les filtres
                      </button>
                    )}
                </div>
              ) : viewMode === "grid" ? (
                <>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {currentEvents.map((event) => renderGridItem(event))}
                  </div>
                  {filteredEvents.length > itemsPerPage && renderPagination()}
                </>
              ) : (
                <>
                  <div className="space-y-6">
                    {currentEvents.map((event) => renderListItem(event))}
                  </div>
                  {filteredEvents.length > itemsPerPage && renderPagination()}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Modals */}
      <Modal isOpen={showForm} onClose={closeForm} width="2xl">
        {formContent}
      </Modal>
    </div>
  );
};

export default Events;
